import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Button, Alert, Badge } from 'react-bootstrap';
import '../styles/Home.css';

const Home = () => {
  // State for cancellation checking
  const [cancellationInfo, setCancellationInfo] = useState(null);
  const [checkingCancellation, setCheckingCancellation] = useState(true);
  
  // State for recurring events
  const [recurringEvents, setRecurringEvents] = useState([]);
  const [recurringEventsLoading, setRecurringEventsLoading] = useState(true);

  // Google Calendar API details (same as Events page)
  const CALENDAR_ID = process.env.REACT_APP_GOOGLE_CALENDAR_ID;
  const API_KEY = process.env.REACT_APP_GOOGLE_CALENDAR_API_KEY;
  const TIMEZONE = 'US/Central';

  // Helper function to format time from Date object
  const formatTime = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: TIMEZONE
    }).format(date);
  };

  // Helper function to format date in Central Time
  const formatDate = (date, options) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      ...options,
      timeZone: TIMEZONE
    });
  };

  // Parse RRULE string to extract recurrence information
  const parseRecurrence = (recurrenceArray) => {
    if (!recurrenceArray || recurrenceArray.length === 0) {
      return null;
    }
    
    const rrule = recurrenceArray[0]; // Get the first RRULE
    const recurrenceInfo = {
      frequency: null,
      interval: 1,
      daysOfWeek: [],
      endDate: null,
      count: null,
      until: null,
      originalRrule: rrule
    };
    
    // Parse FREQ (frequency)
    const freqMatch = rrule.match(/FREQ=([^;]+)/);
    if (freqMatch) {
      recurrenceInfo.frequency = freqMatch[1].toLowerCase();
    }
    
    // Parse INTERVAL
    const intervalMatch = rrule.match(/INTERVAL=(\d+)/);
    if (intervalMatch) {
      recurrenceInfo.interval = parseInt(intervalMatch[1]);
    }
    
    // Parse BYDAY (days of week)
    const bydayMatch = rrule.match(/BYDAY=([^;]+)/);
    if (bydayMatch) {
      recurrenceInfo.daysOfWeek = bydayMatch[1].split(',');
    }
    
    // Parse UNTIL (end date)
    const untilMatch = rrule.match(/UNTIL=([^;]+)/);
    if (untilMatch) {
      recurrenceInfo.until = untilMatch[1];
      recurrenceInfo.endDate = new Date(untilMatch[1].replace(/(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/, '$1-$2-$3T$4:$5:$6Z'));
    }
    
    // Parse COUNT (number of occurrences)
    const countMatch = rrule.match(/COUNT=(\d+)/);
    if (countMatch) {
      recurrenceInfo.count = parseInt(countMatch[1]);
    }
    
    return recurrenceInfo;
  };

  // Format human-readable recurrence description
  const getRecurrenceDescription = (recurrenceInfo) => {
    if (!recurrenceInfo) return null;
    
    const { frequency, interval, daysOfWeek, endDate, count } = recurrenceInfo;
    
    let description = '';
    
    // Base frequency description
    if (frequency === 'weekly') {
      if (interval === 1) {
        description = 'Weekly';
      } else {
        description = `Every ${interval} weeks`;
      }
      
      // Add day of week for weekly events
      if (daysOfWeek.length > 0) {
        const dayNames = {
          'SU': 'Sunday',
          'MO': 'Monday',
          'TU': 'Tuesday',
          'WE': 'Wednesday',
          'TH': 'Thursday',
          'FR': 'Friday',
          'SA': 'Saturday'
        };
        const daysList = daysOfWeek.map(day => dayNames[day] || day).join(', ');
        description += ` on ${daysList}`;
      }
    } else if (frequency === 'daily') {
      if (interval === 1) {
        description = 'Daily';
      } else {
        description = `Every ${interval} days`;
      }
    } else if (frequency === 'monthly') {
      if (interval === 1) {
        description = 'Monthly';
      } else {
        description = `Every ${interval} months`;
      }
    } else if (frequency === 'yearly') {
      if (interval === 1) {
        description = 'Yearly';
      } else {
        description = `Every ${interval} years`;
      }
    } else {
      description = frequency ? frequency.charAt(0).toUpperCase() + frequency.slice(1) : 'Recurring';
    }
    
    // Add end condition
    if (endDate) {
      description += ` until ${endDate.toLocaleDateString('en-US', { timeZone: TIMEZONE })}`;
    } else if (count) {
      description += ` for ${count} occurrences`;
    }
    
    return description;
  };

  // Animation effect for the welcome section
  useEffect(() => {
    const fadeIn = () => {
      const welcomeSection = document.querySelector('.welcome-section');
      if (welcomeSection) {
        welcomeSection.classList.add('visible');
      }
    };

    // Trigger animation after a short delay
    const timer = setTimeout(() => {
      fadeIn();
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // Fetch recurring events and check for cancellations
  useEffect(() => {
    const fetchRecurringEventsAndCancellations = async () => {
      setRecurringEventsLoading(true);
      setCheckingCancellation(true);
      
      try {
        const today = new Date();
        const eightWeeksFromNow = new Date(today);
        eightWeeksFromNow.setDate(today.getDate() + 56); // 8 weeks
        
        const timeMin = today.toISOString();
        const timeMax = eightWeeksFromNow.toISOString();
        
        console.log('Fetching recurring events from:', timeMin, 'to:', timeMax);
        
        // Fetch recurring events (master events with recurrence rules)
        const recurringUrl = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(CALENDAR_ID)}/events?key=${API_KEY}&timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=false&timeZone=${TIMEZONE}`;
        
        // Fetch individual events for the next 8 weeks
        const eventsUrl = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(CALENDAR_ID)}/events?key=${API_KEY}&timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&orderBy=startTime&timeZone=${TIMEZONE}`;
        
        console.log('Recurring events URL:', recurringUrl);
        console.log('Individual events URL:', eventsUrl);
        
        const [recurringResponse, eventsResponse] = await Promise.all([
          fetch(recurringUrl),
          fetch(eventsUrl)
        ]);
        
        console.log('Response status codes:', {
          recurringStatus: recurringResponse.status,
          eventsStatus: eventsResponse.status,
          recurringStatusText: recurringResponse.statusText,
          eventsStatusText: eventsResponse.statusText
        });
        
        if (!recurringResponse.ok) {
          const recurringErrorText = await recurringResponse.text();
          console.error('Recurring events API error:', {
            status: recurringResponse.status,
            statusText: recurringResponse.statusText,
            errorBody: recurringErrorText
          });
        }
        
        if (!eventsResponse.ok) {
          const eventsErrorText = await eventsResponse.text();
          console.error('Individual events API error:', {
            status: eventsResponse.status,
            statusText: eventsResponse.statusText,
            errorBody: eventsErrorText
          });
        }
        
        if (!recurringResponse.ok || !eventsResponse.ok) {
          throw new Error(`API requests failed - Recurring: ${recurringResponse.status}, Events: ${eventsResponse.status}`);
        }
        
        const [recurringData, eventsData] = await Promise.all([
          recurringResponse.json(),
          eventsResponse.json()
        ]);
        
        console.log('Raw recurring data:', recurringData);
        console.log('Raw events data:', eventsData);
        console.log('Total recurring items found:', recurringData.items?.length || 0);
        console.log('Total individual events found:', eventsData.items?.length || 0);
        
        // Check each recurring item
        recurringData.items?.forEach((item, index) => {
          console.log(`Recurring item ${index}:`, {
            id: item.id,
            summary: item.summary,
            hasRecurrence: !!item.recurrence,
            recurrence: item.recurrence,
            start: item.start,
            end: item.end
          });
        });
        
        // Process recurring events - be more flexible with filtering
        const formattedRecurringEvents = (recurringData.items || [])
          .filter(item => {
            const hasRecurrence = item.recurrence && item.recurrence.length > 0;
            const isNotDeleted = item.status !== 'cancelled';
            console.log(`Item "${item.summary}" has recurrence:`, hasRecurrence, 'status:', item.status, 'not deleted:', isNotDeleted);
            return hasRecurrence && isNotDeleted;
          })
          .map(item => {
            const startDateTime = item.start.dateTime || item.start.date;
            const endDateTime = item.end.dateTime || item.end.date;
            const startDate = new Date(startDateTime);
            const endDate = new Date(endDateTime);
            
            console.log('Processing recurring event:', {
              title: item.summary,
              startDateTime,
              endDateTime,
              recurrence: item.recurrence
            });
            
            // Parse recurrence information
            const recurrenceInfo = parseRecurrence(item.recurrence);
            const recurrenceDescription = getRecurrenceDescription(recurrenceInfo);
            
            console.log('Parsed recurrence info:', recurrenceInfo);
            console.log('Recurrence description:', recurrenceDescription);
            
            return {
              id: item.id,
              title: item.summary || 'Untitled Event',
              description: item.description || 'No description available',
              location: item.location || 'TBD',
              locationInfo: formatLocation(item.location),
              time: `${formatTime(startDate)} - ${formatTime(endDate)}`,
              originalStartDate: startDate,
              originalEndDate: endDate,
              recurrence: recurrenceInfo,
              recurrenceDescription: recurrenceDescription,
              isRecurring: true
            };
          });
        
        console.log('Formatted recurring events:', formattedRecurringEvents);
        
        // Process individual events to check for cancellations
        const individualEvents = (eventsData.items || [])
          .filter(item => item.status !== 'cancelled') // Filter out deleted events
          .map(item => {
            const startDateTime = item.start.dateTime || item.start.date;
            const startDate = new Date(startDateTime);
            
            return {
              id: item.id,
              title: item.summary || '',
              description: item.description || '',
              startDate: startDate,
              recurringEventId: item.recurringEventId || null,
              isCancelled: (item.summary && item.summary.toLowerCase().includes('cancelled')) ||
                          (item.description && item.description.toLowerCase().includes('cancelled'))
            };
          });
        
        console.log('Individual events processed:', individualEvents.length);
        
        // For each recurring event, check for cancellations and find next valid event
        const eventsWithCancellationInfo = formattedRecurringEvents.map(recurringEvent => {
          // Find individual events that match this recurring event
          const matchingEvents = individualEvents.filter(event => 
            event.recurringEventId === recurringEvent.id
          );
          
          console.log(`Matching events for "${recurringEvent.title}":`, matchingEvents.length);
          
          // Find cancelled events in the next week
          const oneWeekFromNow = new Date(today);
          oneWeekFromNow.setDate(today.getDate() + 7);
          
          const nextWeekCancelled = matchingEvents.filter(event => 
            event.isCancelled && 
            event.startDate >= today && 
            event.startDate <= oneWeekFromNow
          );
          
          // Find next non-cancelled event
          const nextValidEvent = matchingEvents
            .filter(event => !event.isCancelled && event.startDate >= today)
            .sort((a, b) => a.startDate - b.startDate)[0];
          
          return {
            ...recurringEvent,
            cancelledEvents: nextWeekCancelled,
            nextValidEvent: nextValidEvent,
            hasCancellations: nextWeekCancelled.length > 0
          };
        });
        
        console.log('Final events with cancellation info:', eventsWithCancellationInfo);
        
        setRecurringEvents(eventsWithCancellationInfo);
        
        // Set cancellation info for the main alert (for the first cancelled regular meeting)
        const regularMeetings = individualEvents.filter(event => 
          !event.title || (!event.title.toLowerCase().includes('workshop') && 
                          !event.title.toLowerCase().includes('tournament') && 
                          !event.title.toLowerCase().includes('online'))
        ).sort((a, b) => a.startDate - b.startDate);
        
        if (regularMeetings.length > 0) {
          const nextMeeting = regularMeetings[0];
          
          if (nextMeeting.isCancelled) {
            const nextNonCancelledMeeting = regularMeetings.find(meeting => !meeting.isCancelled);
            
            setCancellationInfo({
              cancelledDate: nextMeeting.startDate.toLocaleDateString('en-US', { 
                weekday: 'long', 
                month: 'long', 
                day: 'numeric'
              }),
              nextMeetingDate: nextNonCancelledMeeting ? nextNonCancelledMeeting.startDate.toLocaleDateString('en-US', { 
                weekday: 'long', 
                month: 'long', 
                day: 'numeric'
              }) : 'TBD'
            });
          } else {
            setCancellationInfo(null);
          }
        } else {
          setCancellationInfo(null);
        }
        
      } catch (error) {
        console.error('Error fetching recurring events:', error);
        console.error('Error details:', error.message, error.stack);
        setRecurringEvents([]);
        setCancellationInfo(null);
      } finally {
        setRecurringEventsLoading(false);
        setCheckingCancellation(false);
      }
    };
    
    fetchRecurringEventsAndCancellations();
  }, [API_KEY, CALENDAR_ID]);

  // Generate Google Calendar URL for an event
  const generateCalendarURL = (event) => {
    // Format dates for Google Calendar (YYYYMMDDTHHMMSSZ format in UTC)
    const formatDateForCalendar = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    const startTimeUTC = formatDateForCalendar(event.originalStartDate);
    const endTimeUTC = formatDateForCalendar(event.originalEndDate);
    
    const eventDetails = {
      text: event.title,
      dates: `${startTimeUTC}/${endTimeUTC}`,
      details: event.description,
      location: event.location
    };
    
    const params = new URLSearchParams(eventDetails);
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&${params.toString()}`;
  };

  // Helper function to detect and format online meeting locations
  const formatLocation = (location) => {
    if (!location) return 'TBD';
    
    // Check if location contains a URL
    const urlRegex = /(https?:\/\/[^\s]+)/gi;
    const urlMatch = location.match(urlRegex);
    
    if (urlMatch) {
      const url = urlMatch[0];
      const prefix = location.replace(urlMatch[0], '').trim().replace(/^:/, '').trim();
      
      return {
        isOnline: true,
        url: url,
        platform: prefix || 'Online',
        displayText: prefix ? `${prefix}` : 'Online Meeting'
      };
    }
    
    // Check for common online meeting indicators without URLs
    const onlineKeywords = ['zoom', 'meet', 'teams', 'discord', 'online', 'virtual', 'remote'];
    const isLikelyOnline = onlineKeywords.some(keyword => 
      location.toLowerCase().includes(keyword)
    );
    
    if (isLikelyOnline) {
      return {
        isOnline: true,
        url: null,
        platform: location,
        displayText: location
      };
    }
    
    return {
      isOnline: false,
      url: null,
      platform: null,
      displayText: location
    };
  };

  return (
    <div className="home-page">
      {/* Cancellation Notice Widget */}
      {!checkingCancellation && cancellationInfo && (
        <Alert variant="warning" className="cancellation-notice mb-4">
          <Alert.Heading className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Meeting Cancelled
          </Alert.Heading>
          <p className="mb-2">
            <strong>The regular meeting on {cancellationInfo.cancelledDate} has been cancelled.</strong>
          </p>
          <p className="mb-0">
            <i className="bi bi-calendar-check me-1"></i>
            The next meeting will be on <strong>{cancellationInfo.nextMeetingDate}</strong> at 2:00 PM.
          </p>
        </Alert>
      )}

      <section className="welcome-section">
        <Row className="align-items-center">
          <Col md={8}>
            <h2>Welcome to the Birmingham Go Club</h2>
            <p className="lead">
              Join our community dedicated to playing and enjoying Go—a game with simple rules but fascinating strategic depth.
            </p>

            <div className="game-description mt-3 mb-4">
              <p>
                Go is the oldest board game still played in its original form today. With rules simple enough to learn in minutes, 
                it offers a lifetime of interesting patterns and possibilities. Discover the subtlety and wisdom of a board game that has captivated minds for over 2,500 years.
              </p>
            </div>
          </Col>
          <Col md={4}>
            <div className="welcome-image">
              <img 
                src="/resources/images/club/Regular-meeting.jpeg" 
                alt="Birmingham Go Club regular Sunday meeting with members playing" 
                className="img-fluid rounded"
                onError={(e) => {
                  e.target.src = "https://via.placeholder.com/400x300/2c5f41/ffffff?text=Regular+Meeting";
                }}
              />
              <small className="text-muted text-center d-block mt-2">
                Our regular Sunday meetings bring together Go enthusiasts of all skill levels
              </small>
            </div>
          </Col>
        </Row>
        
        {/* Learn to Play Section - moved above Weekly Meetings */}
        <Row className="mt-4">
          <Col md={6} className="mb-4">
            <Card className="info-card">
              <Card.Body>
                <Card.Title>Learn to Play</Card.Title>
                <Card.Text>
                  New to Go? We have players who are eager to teach beginners and help you get started.
                  Go is a fun and rewarding game that anyone can learn and enjoy.
                </Card.Text>
                <Button variant="primary" href="/howtoplay">Learn to Play</Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        
        {/* Dynamic Recurring Events Section */}
        <div className="recurring-events-section mt-4">
          <div className="d-flex align-items-center gap-3 mb-3">
            <h3 className="mb-0">Regular Meetings</h3>
            <Button variant="primary" size="sm" href="/events">View Schedule</Button>
          </div>
          
          {recurringEventsLoading ? (
            <div className="text-center my-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading meetings...</span>
              </div>
              <p className="mt-3">Loading meeting information...</p>
            </div>
          ) : recurringEvents.length === 0 ? (
            <Card className="info-card">
              <Card.Body>
                <Card.Title>Regular Meetings</Card.Title>
                <Card.Text>
                  We meet every Sunday from 2:00 PM to 6:00 PM at <strong>Santos, 1021 Brocks Gap Pkwy, Suite 101, Hoover, AL 35244</strong>.
                  All levels are welcome - from complete beginners to experienced players. Boards and stones are provided.
                </Card.Text>
                <Button variant="primary" href="/events">View Schedule</Button>
              </Card.Body>
            </Card>
          ) : (
            <div className="recurring-events-grid">
              {/* Desktop: horizontal layout, Mobile: vertical layout */}
              <div className="d-none d-md-flex flex-row gap-3 overflow-auto">
                {recurringEvents.map((event, index) => (
                  <div key={event.id} className="flex-shrink-0" style={{ width: '350px' }}>
                    <Card className={`h-100 info-card ${event.hasCancellations ? 'cancelled-meeting-card' : ''}`}>
                      <Card.Body className="d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <Card.Title className="h6">
                            {event.title}
                            {event.hasCancellations && (
                              <Badge bg="warning" className="ms-2">
                                <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                Cancellation
                              </Badge>
                            )}
                          </Card.Title>
                        </div>
                        
                        <Card.Subtitle className="mb-2 text-muted small">
                          <i className="bi bi-arrow-repeat me-1"></i>
                          {event.recurrenceDescription}
                        </Card.Subtitle>
                        
                        <Card.Text className="small mb-2">
                          <strong>Time:</strong> {event.time}<br />
                          <strong>Location:</strong> {
                            event.locationInfo.isOnline ? (
                              <span>
                                <i className="bi bi-camera-video me-1"></i>
                                {event.locationInfo.url ? (
                                  <a 
                                    href={event.locationInfo.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-primary"
                                  >
                                    {event.locationInfo.displayText}
                                  </a>
                                ) : (
                                  <span className="text-info">{event.locationInfo.displayText}</span>
                                )}
                              </span>
                            ) : (
                              <span>
                                <i className="bi bi-geo-alt me-1"></i>
                                {event.locationInfo.displayText}
                              </span>
                            )
                          }
                        </Card.Text>
                        
                        <Card.Text className="small mb-3 flex-grow-1">
                          {event.description}
                        </Card.Text>
                        
                        {/* Cancellation notices */}
                        {event.hasCancellations && (
                          <Alert variant="warning" className="py-2 mb-2">
                            <small>
                              <strong>Cancelled:</strong>
                              {event.cancelledEvents.map((cancelled, idx) => (
                                <div key={idx}>
                                  {formatDate(cancelled.startDate, { weekday: 'long', month: 'long', day: 'numeric' })}
                                  {cancelled.title && ` (${cancelled.title})`}
                                </div>
                              ))}
                            </small>
                          </Alert>
                        )}
                        
                        {/* Next valid event */}
                        {event.nextValidEvent && (
                          <Alert variant="info" className="py-2 mb-3">
                            <small>
                              <strong>Next meeting:</strong> {formatDate(event.nextValidEvent.startDate, { weekday: 'long', month: 'long', day: 'numeric' })}
                            </small>
                          </Alert>
                        )}
                        
                        <div className="d-flex gap-2 flex-wrap mt-auto">
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            href={generateCalendarURL(event)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Add to Calendar
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                ))}
              </div>
              
              {/* Mobile: vertical layout */}
              <div className="d-md-none">
                <Row>
                  {recurringEvents.map((event, index) => (
                    <Col xs={12} key={event.id} className="mb-3">
                      <Card className={`info-card ${event.hasCancellations ? 'cancelled-meeting-card' : ''}`}>
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <Card.Title className="h6">
                              {event.title}
                              {event.hasCancellations && (
                                <Badge bg="warning" className="ms-2">
                                  <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                  Cancellation
                                </Badge>
                              )}
                            </Card.Title>
                          </div>
                          
                          <Card.Subtitle className="mb-2 text-muted small">
                            <i className="bi bi-arrow-repeat me-1"></i>
                            {event.recurrenceDescription}
                          </Card.Subtitle>
                          
                          <Card.Text className="small mb-2">
                            <strong>Time:</strong> {event.time}<br />
                            <strong>Location:</strong> {
                              event.locationInfo.isOnline ? (
                                <span>
                                  <i className="bi bi-camera-video me-1"></i>
                                  {event.locationInfo.url ? (
                                    <a 
                                      href={event.locationInfo.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-primary"
                                    >
                                      {event.locationInfo.displayText}
                                    </a>
                                  ) : (
                                    <span className="text-info">{event.locationInfo.displayText}</span>
                                  )}
                                </span>
                              ) : (
                                <span>
                                  <i className="bi bi-geo-alt me-1"></i>
                                  {event.locationInfo.displayText}
                                </span>
                              )
                            }
                          </Card.Text>
                          
                          <Card.Text className="small mb-3">
                            {event.description}
                          </Card.Text>
                          
                          {/* Cancellation notices */}
                          {event.hasCancellations && (
                            <Alert variant="warning" className="py-2 mb-2">
                              <small>
                                <strong>Cancelled:</strong>
                                {event.cancelledEvents.map((cancelled, idx) => (
                                  <div key={idx}>
                                    {formatDate(cancelled.startDate, { weekday: 'long', month: 'long', day: 'numeric' })}
                                    {cancelled.title && ` (${cancelled.title})`}
                                  </div>
                                ))}
                              </small>
                            </Alert>
                          )}
                          
                          {/* Next valid event */}
                          {event.nextValidEvent && (
                            <Alert variant="info" className="py-2 mb-3">
                              <small>
                                <strong>Next meeting:</strong> {formatDate(event.nextValidEvent.startDate, { weekday: 'long', month: 'long', day: 'numeric' })}
                              </small>
                            </Alert>
                          )}
                          
                          <div className="d-flex gap-2 flex-wrap">
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              href={generateCalendarURL(event)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Add to Calendar
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="community-showcase mt-5">
        <h3>Our Community</h3>
        <Row className="g-4">
          <Col md={6}>
            <div className="community-image">
              <img 
                src="/resources/images/club/Atlanta-tournament-afterparty.jpeg" 
                alt="Birmingham Go Club members at Atlanta tournament afterparty" 
                className="img-fluid rounded"
                onError={(e) => {
                  e.target.src = "https://via.placeholder.com/500x300/3a7ca5/ffffff?text=Tournament+Afterparty";
                }}
              />
              <small className="text-muted">
                We travel to tournaments and build lasting friendships
              </small>
            </div>
          </Col>
          <Col md={6}>
            <div className="community-image">
              <img 
                src="/resources/images/club/Regular-meeting-2.jpeg" 
                alt="Birmingham Go Club regular Sunday meeting" 
                className="img-fluid rounded"
                onError={(e) => {
                  e.target.src = "https://via.placeholder.com/500x300/5a8c7a/ffffff?text=Regular+Meeting";
                }}
              />
              <small className="text-muted">
                Our welcoming Sunday meetings where everyone learns and plays together
              </small>
            </div>
          </Col>
        </Row>
      </section>

      <section className="join-section mt-5 text-center">
        <h3>Join Our Community</h3>
        <p>
          Whether you're an experienced player or completely new to Go,
          we'd love to have you join us. It's completely free and you'll get access to our equipment, library, and friendly community.
        </p>
        <p className="mt-3 mb-4">
          <em>"While the Baroque rules of Chess could only have been created by humans, the rules of Go are so elegant, organic, and rigorously logical that if intelligent life forms exist elsewhere in the universe, they almost certainly play Go."</em> — Edward Lasker
        </p>
        <Button variant="success" size="lg" href="/contact">Contact Us</Button>
      </section>
    </div>
  );
};

export default Home; 